import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, FileText, Download, Heart, Shield, History, FileDown, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_COLOR = {
  new: "bg-stone text-forest",
  in_review: "bg-warning/15 text-warning",
  approved: "bg-success/15 text-success",
  rejected: "bg-danger/15 text-danger",
};

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="text-forest text-sm">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-10 bg-white border-line input-clean"
        data-testid={testId}
      />
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [qr, setQr] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, aRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/audit`),
      ]);
      setClient(cRes.data);
      setAudit(aRes.data || []);
    } catch {
      toast.error("Client not found");
      navigate("/agent/clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const update = (field, value) => setClient((c) => ({ ...c, [field]: value }));
  const updateNested = (group, field, value) =>
    setClient((c) => ({ ...c, [group]: { ...(c[group] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        full_name: client.full_name,
        email: client.email,
        phone: client.phone,
        dob: client.dob,
        gender: client.gender,
        address: client.address,
        city: client.city,
        state: client.state,
        postal_code: client.postal_code,
        id_number: client.id_number,
        notes: client.notes,
        status: client.status,
        health_details: client.health_details,
        life_details: client.life_details,
      };
      const { data } = await api.patch(`/clients/${id}`, payload);
      setClient({ ...client, ...data });
      const auditResp = await api.get(`/clients/${id}/audit`);
      setAudit(auditResp.data || []);
      toast.success("Client updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (file) => {
    try {
      const token = localStorage.getItem("if_token");
      const resp = await fetch(`${API}/files/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("if_token");
      const resp = await fetch(`${API}/clients/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (client.full_name || "client").replace(/\s+/g, "-");
      a.download = `${safe}-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setPdfLoading(false);
    }
  };

  const showQr = async () => {
    setQrOpen(true);
    if (qr) return;
    try {
      const { data } = await api.get(`/clients/${id}/qr`);
      setQr(data);
    } catch {
      toast.error("Failed to load QR code");
      setQrOpen(false);
    }
  };

  if (loading || !client) {
    return <div className="p-10 text-sage">Loading…</div>;
  }

  const isHealth = client.insurance_type === "health";

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <Link
        to="/agent/clients"
        className="text-sm text-sage hover:text-forest inline-flex items-center gap-1 mb-6 transition-colors duration-200"
        data-testid="back-to-clients-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </Link>

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            {isHealth ? (
              <Heart className="w-5 h-5 text-clay" strokeWidth={1.7} />
            ) : (
              <Shield className="w-5 h-5 text-clay" strokeWidth={1.7} />
            )}
            <span className="text-xs uppercase tracking-[0.2em] text-clay font-semibold">
              {isHealth ? "Health Insurance" : "Life Insurance"}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[client.status]}`}>
              {STATUS_OPTIONS.find((s) => s.value === client.status)?.label}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium" data-testid="client-name-heading">
            {client.full_name}
          </h1>
          <p className="text-sage mt-1 text-sm">Submitted {new Date(client.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={showQr}
            variant="outline"
            className="border-line text-forest hover:bg-cream rounded-sm h-10 px-4"
            data-testid="show-qr-btn"
          >
            <QrCode className="w-4 h-4 mr-2" /> Tracker QR
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={pdfLoading}
            variant="outline"
            className="border-clay text-clay hover:bg-clay hover:text-bone rounded-sm h-10 px-4"
            data-testid="download-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" /> {pdfLoading ? "Generating…" : "Download PDF"}
          </Button>
          <Select value={client.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger className="w-44 h-10 bg-white border-line" data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-forest hover:bg-forestdark text-bone rounded-sm h-10 px-5"
            data-testid="save-client-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Personal */}
        <section className="lg:col-span-2 bg-white border border-line rounded-sm p-6">
          <h2 className="font-heading text-xl mb-5">Personal information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" value={client.full_name} onChange={(v) => update("full_name", v)} testId="edit-full-name" />
            <Field label="Email" value={client.email} onChange={(v) => update("email", v)} type="email" testId="edit-email" />
            <Field label="Phone" value={client.phone} onChange={(v) => update("phone", v)} testId="edit-phone" />
            <div>
              <Label className="text-forest text-sm">Date of birth</Label>
              <div className="mt-1.5">
                <DatePicker
                  value={client.dob || ""}
                  onChange={(v) => update("dob", v)}
                  testId="edit-dob"
                />
              </div>
            </div>
            <div>
              <Label className="text-forest text-sm">Gender</Label>
              <Select value={client.gender} onValueChange={(v) => update("gender", v)}>
                <SelectTrigger className="mt-1.5 h-10 bg-white border-line" data-testid="edit-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Government ID #" value={client.id_number} onChange={(v) => update("id_number", v)} testId="edit-id-number" />
            <div className="md:col-span-2">
              <Field label="Address" value={client.address} onChange={(v) => update("address", v)} testId="edit-address" />
            </div>
            <Field label="City" value={client.city} onChange={(v) => update("city", v)} testId="edit-city" />
            <Field label="State" value={client.state} onChange={(v) => update("state", v)} testId="edit-state" />
            <Field label="Postal code" value={client.postal_code} onChange={(v) => update("postal_code", v)} testId="edit-postal-code" />
            <div className="md:col-span-2">
              <Label className="text-forest text-sm">Notes</Label>
              <Textarea
                value={client.notes || ""}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                className="mt-1.5 bg-white border-line input-clean resize-none"
                data-testid="edit-notes"
              />
            </div>
          </div>
        </section>

        {/* Documents */}
        <section className="bg-white border border-line rounded-sm p-6">
          <h2 className="font-heading text-xl mb-5">Documents</h2>
          {client.files?.length ? (
            <ul className="space-y-2" data-testid="client-files-list">
              {client.files.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between bg-cream/40 border border-line rounded-sm px-3 py-2.5"
                  data-testid={`file-item-${f.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-clay flex-shrink-0" />
                    <span className="text-sm text-forest truncate">{f.original_filename}</span>
                  </div>
                  <button
                    onClick={() => downloadFile(f)}
                    className="text-clay hover:text-claydark flex-shrink-0"
                    data-testid={`download-file-${f.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-sage">No documents attached.</p>
          )}
        </section>

        {/* Insurance specific */}
        {isHealth ? (
          <section className="lg:col-span-3 bg-white border border-line rounded-sm p-6">
            <h2 className="font-heading text-xl mb-5">Health profile</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Height (cm)" value={client.health_details?.height_cm} onChange={(v) => updateNested("health_details", "height_cm", v)} type="number" testId="edit-height" />
              <Field label="Weight (kg)" value={client.health_details?.weight_kg} onChange={(v) => updateNested("health_details", "weight_kg", v)} type="number" testId="edit-weight" />
              <Field label="Blood group" value={client.health_details?.blood_group} onChange={(v) => updateNested("health_details", "blood_group", v)} testId="edit-blood-group" />
              <Field label="Coverage amount (USD)" value={client.health_details?.coverage_amount} onChange={(v) => updateNested("health_details", "coverage_amount", v)} type="number" testId="edit-coverage" />
              <Field label="Exercise" value={client.health_details?.exercise_frequency} onChange={(v) => updateNested("health_details", "exercise_frequency", v)} testId="edit-exercise" />
              <Field label="Alcohol" value={client.health_details?.alcohol_consumption} onChange={(v) => updateNested("health_details", "alcohol_consumption", v)} testId="edit-alcohol" />
              <div className="md:col-span-3">
                <Label className="text-forest text-sm">Pre-existing conditions</Label>
                <Textarea
                  value={client.health_details?.pre_existing_conditions || ""}
                  onChange={(e) => updateNested("health_details", "pre_existing_conditions", e.target.value)}
                  rows={2}
                  className="mt-1.5 bg-white border-line input-clean resize-none"
                  data-testid="edit-conditions"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-forest text-sm">Current medications</Label>
                <Textarea
                  value={client.health_details?.current_medications || ""}
                  onChange={(e) => updateNested("health_details", "current_medications", e.target.value)}
                  rows={2}
                  className="mt-1.5 bg-white border-line input-clean resize-none"
                  data-testid="edit-medications"
                />
              </div>
            </div>
          </section>
        ) : (
          <section className="lg:col-span-3 bg-white border border-line rounded-sm p-6">
            <h2 className="font-heading text-xl mb-5">Life profile & beneficiary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Occupation" value={client.life_details?.occupation} onChange={(v) => updateNested("life_details", "occupation", v)} testId="edit-occupation" />
              <Field label="Annual income (USD)" value={client.life_details?.annual_income} onChange={(v) => updateNested("life_details", "annual_income", v)} type="number" testId="edit-income" />
              <Field label="Coverage amount (USD)" value={client.life_details?.coverage_amount} onChange={(v) => updateNested("life_details", "coverage_amount", v)} type="number" testId="edit-coverage" />
              <Field label="Policy term (years)" value={client.life_details?.policy_term_years} onChange={(v) => updateNested("life_details", "policy_term_years", v)} type="number" testId="edit-policy-term" />
              <Field label="Beneficiary name" value={client.life_details?.beneficiary_name} onChange={(v) => updateNested("life_details", "beneficiary_name", v)} testId="edit-beneficiary-name" />
              <Field label="Beneficiary relation" value={client.life_details?.beneficiary_relation} onChange={(v) => updateNested("life_details", "beneficiary_relation", v)} testId="edit-beneficiary-relation" />
              <div>
                <Label className="text-forest text-sm">Beneficiary DOB</Label>
                <div className="mt-1.5">
                  <DatePicker
                    value={client.life_details?.beneficiary_dob || ""}
                    onChange={(v) => updateNested("life_details", "beneficiary_dob", v)}
                    testId="edit-beneficiary-dob"
                  />
                </div>
              </div>
              <div className="md:col-span-3">
                <Label className="text-forest text-sm">High risk activities</Label>
                <Textarea
                  value={client.life_details?.high_risk_activities || ""}
                  onChange={(e) => updateNested("life_details", "high_risk_activities", e.target.value)}
                  rows={2}
                  className="mt-1.5 bg-white border-line input-clean resize-none"
                  data-testid="edit-risk-activities"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-forest text-sm">Existing policies</Label>
                <Textarea
                  value={client.life_details?.existing_policies || ""}
                  onChange={(e) => updateNested("life_details", "existing_policies", e.target.value)}
                  rows={2}
                  className="mt-1.5 bg-white border-line input-clean resize-none"
                  data-testid="edit-existing-policies"
                />
              </div>
            </div>
          </section>
        )}

        {client.custom_fields && Object.keys(client.custom_fields).length > 0 && (
          <section className="lg:col-span-3 bg-white border border-line rounded-sm p-6" data-testid="custom-fields-display">
            <h2 className="font-heading text-xl mb-5">Additional information</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(client.custom_fields).map(([k, v]) => (
                <div key={k} data-testid={`custom-display-${k}`}>
                  <dt className="text-xs uppercase tracking-[0.15em] text-sage font-medium mb-1">
                    {k.replace(/_/g, " ")}
                  </dt>
                  <dd className="text-forest text-sm whitespace-pre-line">
                    {v === true ? "Yes" : v === false ? "No" : Array.isArray(v) ? v.join(", ") : (v || "—")}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Audit log */}
        <section className="lg:col-span-3 bg-white border border-line rounded-sm p-6" data-testid="audit-log-section">
          <div className="flex items-center gap-2 mb-5">
            <History className="w-4 h-4 text-clay" />
            <h2 className="font-heading text-xl">Activity log</h2>
          </div>
          {audit.length === 0 ? (
            <p className="text-sm text-sage">No activity yet. Status changes and edits will appear here.</p>
          ) : (
            <ul className="divide-y divide-line">
              {audit.map((a) => (
                <li key={a.id} className="py-3 flex items-start justify-between gap-4" data-testid={`audit-item-${a.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-forest">
                      <span className="font-medium">{a.agent_name}</span>{" "}
                      {a.action === "status_change" ? (
                        <>
                          changed status from{" "}
                          <span className="capitalize">{(a.details?.from || "—").replace("_", " ")}</span> to{" "}
                          <span className="capitalize font-medium text-clay">{(a.details?.to || "—").replace("_", " ")}</span>
                        </>
                      ) : a.action === "edit" ? (
                        <>edited {(a.details?.fields || []).join(", ") || "client info"}</>
                      ) : (
                        a.action
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-sage whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-bone border border-line rounded-sm max-w-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Client tracker QR</DialogTitle>
          </DialogHeader>
          {qr ? (
            <div className="text-center">
              <img
                src={qr.data_url}
                alt="Tracker QR"
                className="w-56 h-56 mx-auto bg-white p-3 border border-line rounded-sm"
                data-testid="qr-image"
              />
              <p className="text-xs text-sage mt-3 break-all" data-testid="qr-tracker-url">{qr.tracker_url}</p>
              <p className="text-xs text-sage mt-2">
                Share or print this code — anyone scanning it opens the live status page (no login required).
              </p>
            </div>
          ) : (
            <div className="text-sage text-sm py-8 text-center">Loading QR…</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
